import { ChevronRight } from "lucide-react";
import CTASection from "@/components/CTASection";
import {
  FACTORY_STATS,
  WHO_WE_ARE,
  FOUNDERS,
  PROCESS_STEPS,
  PARTNER_CATEGORIES,
  COMPANY,
} from "@/lib/siteData";

const capabilities = [
  "Precision machines for panel processing",
  "Options of marine ply / BWP / BWR and HDHMR ply for carcass construction",
  "Laminate, acrylic and PVC finishes across all categories",
  "Ebco / Ozone / Hettich / Hafele hardware — soft-close as standard",
  "Site measurement, delivery and installation by trained crews",
];

const TONE_MAP = {
  navy: "bg-navy text-white",
  teal: "bg-teal text-white",
  coral: "bg-coral text-white",
};

const FounderAvatar = ({ photo, initials, tone, name }) => {
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        loading="lazy"
        className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover"
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className={`w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center font-display text-3xl md:text-4xl tracking-wider ${TONE_MAP[tone] || TONE_MAP.navy}`}
    >
      {initials}
    </div>
  );
};

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-ivory">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 reveal">
            <div className="overline mb-5">About</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light">
              Made in India.
              <br />
              <span className="italic text-navy">Designed for anywhere.</span>
            </h1>
          </div>
          <p className="lg:col-span-5 text-base md:text-lg text-black/70 leading-relaxed reveal">
            {COMPANY.legal} began manufacturing modular furniture in {COMPANY.established}. Three years in, we are the reason architects, designers, and dealers no longer need to look outside for production-grade modular work.
          </p>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section data-testid="who-we-are" className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Who we are</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                A modular furniture manufacturer<br />
                <span className="italic text-navy">focused on real value.</span>
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/70 leading-relaxed">
              High-quality, functional, and value-driven solutions for modern Indian homes and workspaces.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {WHO_WE_ARE.map((p, i) => (
              <div key={p.title} className="reveal bg-ivory p-8 md:p-10 border-t-4 border-teal" style={{ transitionDelay: `${i * 70}ms` }}>
                <div className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{p.title}</div>
                <p className="mt-4 text-base text-black/70 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NUMBERS / TRACK RECORD */}
      <section data-testid="track-record" className="bg-offwhite border-y border-brandgray">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <div className="max-w-2xl mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Track record</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              The numbers,<br />
              <span className="italic text-navy">audited by the years</span>.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
            {FACTORY_STATS.map((s, i) => (
              <div key={i} className="reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="font-display text-4xl md:text-5xl lg:text-6xl text-teal leading-none tracking-tight">{s.number}</div>
                <div className="mt-3 text-sm md:text-base text-black/70 leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-12 text-base md:text-lg text-black/70 max-w-2xl reveal">
            Trusted by architects, interior designers, and corporate clients — 100+ projects delivered end-to-end since May 2023.
          </p>
        </div>
      </section>

      {/* MEET THE FOUNDERS */}
      <section data-testid="founders" className="bg-ivory py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20 reveal">
            <div className="max-w-3xl">
              <div className="overline mb-4">Meet the founders</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Founding team from<br />
                <span className="italic text-navy">IIT and IIM</span>, with 15+ years of experience.
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/70 leading-relaxed">
              Commitment-led execution — with transparency and accountability at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {FOUNDERS.map((f, i) => (
              <div key={f.name} data-testid={`founder-${f.initials.toLowerCase()}`} className="reveal bg-white p-8 md:p-10 border border-brandgray shadow-[0_2px_20px_rgba(41,72,144,0.04)] flex flex-col items-start" style={{ transitionDelay: `${i * 90}ms` }}>
                <FounderAvatar {...f} />
                <div className="mt-6 font-display text-2xl md:text-[26px] leading-tight text-black">{f.name}</div>
                <div className="mt-1 text-sm uppercase tracking-[0.24em] text-teal">{f.role}</div>
                <p className="mt-4 text-base text-black/70 leading-relaxed">{f.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR MANUFACTURING (merged with process) */}
      <section data-testid="manufacturing" className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Our manufacturing</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              From raw sheet<br />
              <span className="italic text-navy">to dispatch, in one clean line.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
              Factory-led. In-house control over the entire manufacturing process — from raw sheet to dispatch. Less than 30 days order-to-dispatch turnaround.
            </p>
          </div>

          {/* Process flow */}
          <div className="reveal">
            <div className="overline mb-4">The process</div>
            <ol className="flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-1">
              {PROCESS_STEPS.map((step, i) => (
                <li key={step} data-testid={`process-step-${i + 1}`} className="flex items-center gap-3 lg:gap-1 lg:flex-1">
                  <div className="bg-white border border-brandgray px-3 py-4 lg:px-3 lg:py-4 flex flex-col items-center text-center w-full h-full">
                    <span className="font-display text-teal text-[10px] tracking-[0.24em] uppercase whitespace-nowrap">Step {String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display text-base lg:text-[17px] mt-1 leading-tight text-navy font-semibold">{step}</span>
                  </div>
                  {i < PROCESS_STEPS.length - 1 && (
                    <ChevronRight size={16} className="text-coral shrink-0 hidden lg:block" strokeWidth={1.5} />
                  )}
                </li>
              ))}
            </ol>
          </div>

          {/* Capabilities list */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {capabilities.map((c, i) => (
              <div key={i} className="flex gap-3 items-start border-t border-brandgray pt-4 reveal">
                <span className="mt-1 shrink-0 w-1.5 h-1.5 bg-teal" />
                <span className="text-base text-black/80">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH — categories grid */}
      <section data-testid="who-we-work-with" className="bg-ivory py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Who we work with</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                A partner list that keeps<br />
                <span className="italic text-navy">expanding.</span>
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/70 leading-relaxed">
              We work across a range of B2B partners — the common thread is credibility, delivery discipline, and long-term relationships.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 md:gap-6">
            {PARTNER_CATEGORIES.map((label, i) => {
              const palettes = [
                "bg-navy text-white",
                "bg-teal text-white",
                "bg-coral text-white",
                "bg-gold text-navy",
                "bg-navy text-white",
                "bg-teal text-white",
              ];
              return (
                <div
                  key={label}
                  data-testid={`partner-category-${i}`}
                  className={`reveal ${palettes[i % palettes.length]} rounded-full aspect-square flex items-center justify-center p-6 md:p-8 text-center shadow-[0_10px_30px_rgba(41,72,144,0.10)] hover:scale-[1.03] transition-transform duration-500`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="font-display text-base md:text-lg lg:text-xl leading-tight tracking-tight">{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Come see for yourself"
        title="Factory visits, for those who ask the right questions."
        body="Franchisees and Dealer candidates, Corporates, Institutions, Architects — we welcome walk-throughs by appointment. Bring your questions."
        primaryLabel="Book a Factory Visit"
        primaryTo="/contact"
        secondaryLabel="Become a Dealer"
        secondaryTo="/become-a-dealer"
        testid="about-cta"
      />
    </div>
  );
};

export default AboutPage;
